import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';
import { isBefore, isSameDay, startOfTomorrow } from 'date-fns';
import { Box, Button, ClickAwayListener, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import EventIcon from '@mui/icons-material/Event';

import { DrawerLayoutWrapper } from 'common';

import { AuthContext } from 'authentication/AuthContext';

import { getConfirmationDialog, openConfirmationDialog } from 'store/Confirmationdialogslice';
import AuthSequencingItem from '../templates/AuthSequencingItem';

import {
  fetchAuthorizationSequencingDetails,
  getStoreValueByProp,
  updateFutureAuthorizationSequence,
} from '../store/authorizationSequencingSlice';
import * as futureDrawerStyles from './EditFutureSequenceDrawer.styles';
import { ProductLocationInfo } from './components';

const FUTURE_SEQUENCE_DROPPABLE_ID = 'future-sequenced-authorizations';
const FUTURE_AVAILABLE_DROPPABLE_ID = 'future-available-authorizations';

const getAuthorizationKey = ({ productAuthorizationId, supplierDirectedBlend } = {}) =>
  `${productAuthorizationId}-${supplierDirectedBlend ? 'SDB' : 'STD'}`;

const sortByInventoryOwner = authorizations =>
  [...authorizations].sort((a, b) =>
    (a?.inventoryOwner?.nameShort || '').localeCompare(
      b?.inventoryOwner?.nameShort || '',
      undefined,
      { numeric: true, sensitivity: 'base' }
    )
  );

const EditFutureSequenceDrawer = ({ affiliate, isReadOnly, locationId, onClose, productId }) => {
  const dispatch = useDispatch();
  const detailsValue = useSelector(getStoreValueByProp(['authorizationSequencingDetails']));
  const details = useMemo(() => detailsValue || {}, [detailsValue]);
  const detailsPending = useSelector(
    getStoreValueByProp(['authorizationSequencingDetailsPending'])
  );
  const { dataCompany, location, product } = details;
  const {
    currentAffiliate: {
      dataCompanyId: defaultDataCompanyId,
      customerIdName: defaultCompanyCustomerIdName,
    },
  } = useContext(AuthContext);

  // Use passed affiliate prop, fall back to currentAffiliate from context
  const dataCompanyId = affiliate?.dataCompanyId ?? defaultDataCompanyId;
  const companyCustomerIdName = affiliate?.customerIdName ?? defaultCompanyCustomerIdName;
  const { open: confirmationDialogOpen } = useSelector(getConfirmationDialog);
  const [available, setAvailable] = useState([]);
  const [futureDate, setFutureDate] = useState(null);
  const [futureDateError, setFutureDateError] = useState('');
  const [futureSequenced, setFutureSequenced] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const initialFutureSequencesRef = useRef([]);
  const initialFutureDateRef = useRef(null);

  const hasChanges = useMemo(() => {
    const currentKeys = futureSequenced.map(getAuthorizationKey);
    const initialKeys = (initialFutureSequencesRef.current || []).map(getAuthorizationKey);
    if (currentKeys.length !== initialKeys.length) return true;
    if (currentKeys.some((key, idx) => key !== initialKeys[idx])) return true;

    const initialDate = initialFutureDateRef.current;
    const bothNull = futureDate == null && initialDate == null;
    const bothDates =
      futureDate instanceof Date &&
      initialDate instanceof Date &&
      isSameDay(futureDate, initialDate);
    return !(bothNull || bothDates);
  }, [futureSequenced, futureDate]);
  const title = isReadOnly ? 'Future Sequence' : 'Edit Future Sequence';

  useEffect(() => {
    dispatch(
      fetchAuthorizationSequencingDetails({
        locationId,
        productId,
        supplier: { companyCustomerIdName, dataCompanyId },
      })
    );
  }, [companyCustomerIdName, dataCompanyId, dispatch, locationId, productId]);

  const handleFutureDateError = reason =>
    setFutureDateError(reason ? 'Date must be greater than current date Central Time' : '');

  useEffect(() => {
    if (detailsPending || !location?.id || !product?.id) {
      return;
    }

    const allAuthorizations = [
      ...(details?.sequencedAuthorizations || []),
      ...(details?.unsequencedAuthorizations || []),
    ];

    const initialFutureSequences = allAuthorizations
      .filter(a => !!a.futureSequenceNumber)
      .sort((a, b) => a.futureSequenceNumber - b.futureSequenceNumber);
    const rawDate = initialFutureSequences[0]?.futureSequenceDate;
    const parsedDate = rawDate ? new Date(rawDate) : null;
    const isValidParsedDate = parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime());
    const nextFutureDate = initialFutureSequences.length
      ? (isValidParsedDate && parsedDate) || startOfTomorrow()
      : null;
    const futureKeys = new Set(initialFutureSequences.map(getAuthorizationKey));
    const initialAvailable = sortByInventoryOwner(
      allAuthorizations.filter(a => !futureKeys.has(getAuthorizationKey(a)))
    );

    initialFutureSequencesRef.current = initialFutureSequences;
    initialFutureDateRef.current = nextFutureDate;
    setAvailable(initialAvailable);
    setFutureDate(nextFutureDate);
    handleFutureDateError(nextFutureDate && isBefore(nextFutureDate, startOfTomorrow()));
    setFutureSequenced(initialFutureSequences);
  }, [details, detailsPending, location?.id, product?.id]);

  const futureDateIsValid =
    futureSequenced.length === 0 ||
    (Boolean(futureDate) && !futureDateError && !isBefore(futureDate, startOfTomorrow()));
  const submitDisabled = submitting || !futureDateIsValid || !hasChanges;

  const showUnsavedChangesDialog = useCallback(() => {
    dispatch(
      openConfirmationDialog({
        cancelAction: () => {},
        confirmAction: onClose,
        header: 'Unsaved Changes',
        leftButtonActionType: 'confirm',
        leftButtonText: 'Continue',
        message:
          'Are you sure you want to leave this page? You have unsaved Future Sequence changes.',
        params: {},
        rightButtonActionType: 'cancel',
        rightButtonText: 'Keep Editing',
      })
    );
  }, [dispatch, onClose]);

  const handleHeaderClose = () => {
    if (hasChanges) {
      showUnsavedChangesDialog();
      return;
    }
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const wasSuccessful = await dispatch(
      updateFutureAuthorizationSequence({
        futureSequencedAuthorizationDate: futureDate,
        futureSequencedAuthorizations: futureSequenced,
      })
    );
    setSubmitting(false);
    if (wasSuccessful) {
      onClose();
    }
  };

  const handleSubmitClick = () => {
    dispatch(
      openConfirmationDialog({
        confirmAction: handleSubmit,
        header: 'Save Future Sequence?',
        message: `Please confirm you would like to update the future sequence for ${product?.nameLong} at ${location?.nameLong}.`,
      })
    );
  };

  const handleOnFutureMove = useCallback((source, destination) => {
    setFutureSequenced(current => R.move(source, destination, current));
  }, []);

  const handleOnFutureRemove = useCallback(
    idx => {
      const authorizationToRemove = futureSequenced[idx];
      if (!authorizationToRemove) return;
      const updated = R.remove(idx, 1, futureSequenced);
      setAvailable(sortByInventoryOwner([...available, authorizationToRemove]));
      setFutureSequenced(updated);
      if (!updated.length) {
        setFutureDate(null);
        setFutureDateError('');
      }
    },
    [available, futureSequenced]
  );

  const handleAddToFutureSeq = useCallback(authorization => {
    const key = getAuthorizationKey(authorization);
    setFutureSequenced(current => [...current, authorization]);
    setAvailable(current => current.filter(a => getAuthorizationKey(a) !== key));
    setFutureDate(current => current || startOfTomorrow());
  }, []);

  const handleDragEnd = useCallback(
    result => {
      const { destination, source } = result;
      if (!destination || source.droppableId === destination.droppableId) {
        if (
          destination &&
          source.droppableId === FUTURE_SEQUENCE_DROPPABLE_ID &&
          source.index !== destination.index
        ) {
          handleOnFutureMove(source.index, destination.index);
        }
        return;
      }
      if (destination.droppableId === FUTURE_SEQUENCE_DROPPABLE_ID) {
        const moved = available[source.index];
        if (!moved) return;
        const next = [...futureSequenced];
        next.splice(destination.index, 0, moved);
        setFutureSequenced(next);
        setAvailable(current => current.filter((_, i) => i !== source.index));
        setFutureDate(current => current || startOfTomorrow());
        return;
      }
      handleOnFutureRemove(source.index);
    },
    [available, futureSequenced, handleOnFutureMove, handleOnFutureRemove]
  );

  const handleClickAway = () => {
    if (confirmationDialogOpen) return;
    if (hasChanges) {
      showUnsavedChangesDialog();
    } else {
      onClose();
    }
  };

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={handleClickAway}
      touchEvent="onTouchStart"
    >
      <Box sx={{ height: '100%' }}>
        <DrawerLayoutWrapper
          buttons={
            isReadOnly ? null : (
              <Button
                disabled={submitDisabled}
                onClick={handleSubmitClick}
                sx={futureDrawerStyles.futureDrawerButtonSx}
              >
                Submit Sequence
              </Button>
            )
          }
          isLoading={detailsPending}
          onHeaderButtonClick={handleHeaderClose}
          sx={futureDrawerStyles.futureDrawerLayoutSx}
          title={title}
        >
          <Box flexGrow={1}>
            <ProductLocationInfo
              dataCompany={dataCompany}
              innerSpacing={0.5}
              labelSx={futureDrawerStyles.futureDrawerFieldLabelSx}
              labelVariant="subtitle2"
              location={location}
              product={product}
              secondaryValueSx={futureDrawerStyles.futureDrawerSecondaryValueSx}
              secondaryValueVariant="body1"
              valueSx={futureDrawerStyles.futureDrawerFieldValueSx}
              valueVariant="body1"
            />
            <DragDropContext onDragEnd={handleDragEnd}>
              <Box px={3}>
                <Typography sx={futureDrawerStyles.futureDrawerSectionTitleSx} variant="body1">
                  Future Sequence
                </Typography>
                <Box pb={3}>
                  <DatePicker
                    disabled={isReadOnly || futureSequenced.length === 0}
                    disablePast
                    format="MM/dd/yyyy"
                    id="future-date"
                    label="Future Date"
                    minDate={startOfTomorrow()}
                    onChange={date => setFutureDate(date || null)}
                    onError={handleFutureDateError}
                    shouldDisableDate={date => isBefore(date, startOfTomorrow())}
                    slotProps={{
                      textField: {
                        error: !isReadOnly && Boolean(futureDateError),
                        fullWidth: true,
                        helperText: isReadOnly ? '' : futureDateError,
                        placeholder: 'mm/dd/yyyy',
                      },
                    }}
                    value={futureDate}
                  />
                </Box>
                <Droppable droppableId={FUTURE_SEQUENCE_DROPPABLE_ID}>
                  {(provided, _snapshot) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <Box
                      ref={provided.innerRef}
                      sx={theme => ({
                        backgroundColor: 'graphic.lighter',
                        borderRadius: 0.5,
                        outline: _snapshot.isDraggingOver
                          ? `1px dashed ${theme.palette._next.primary._states.hover}`
                          : 'none',
                      })}
                      {...provided.droppableProps}
                    >
                      {futureSequenced.length > 0 ? (
                        futureSequenced.map(
                          (
                            { inventoryOwner, productAuthorizationId, supplierDirectedBlend },
                            idx
                          ) => {
                            const key = `future-${productAuthorizationId}${
                              supplierDirectedBlend ? '-SDB' : ''
                            }`;
                            return (
                              <Draggable
                                draggableId={key}
                                index={idx}
                                isDragDisabled={isReadOnly}
                                key={key}
                              >
                                {(_provided, snapshot) => (
                                  <div
                                    ref={_provided.innerRef}
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {..._provided.draggableProps}
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {..._provided.dragHandleProps}
                                  >
                                    <AuthSequencingItem
                                      canEdit={!isReadOnly}
                                      futureSequenced
                                      index={idx}
                                      inventoryOwner={inventoryOwner?.nameShort}
                                      isChanged={false}
                                      isDragging={snapshot.isDragging}
                                      onRemoveSeq={handleOnFutureRemove}
                                      sequenced
                                      supplierDirectedBlend={supplierDirectedBlend}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            );
                          }
                        )
                      ) : (
                        <Stack alignItems="center" direction="row" gap={1}>
                          {_snapshot.isDraggingOver ? (
                            <></>
                          ) : (
                            <Box sx={futureDrawerStyles.emptyStateStyles}>
                              <Box sx={futureDrawerStyles.emptyStateContentSx}>
                                <EventIcon sx={futureDrawerStyles.emptyStateIconSx} />
                                <Typography
                                  sx={futureDrawerStyles.emptyStateMessageSx}
                                  variant="body2"
                                >
                                  <strong>Click or drag</strong> an item here to add an
                                  authorization from the list below to enable loading.
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Stack>
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Box>
              <Box px={3}>
                <Typography sx={futureDrawerStyles.futureDrawerSectionTitleSx} variant="body1">
                  Available Authorizations
                </Typography>
                <Droppable droppableId={FUTURE_AVAILABLE_DROPPABLE_ID}>
                  {(provided, _snapshot) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <Box
                      ref={provided.innerRef}
                      sx={theme => ({
                        backgroundColor: 'graphic.lighter',
                        borderRadius: 0.5,
                        outline: _snapshot.isDraggingOver
                          ? `1px dashed ${theme.palette._next.primary._states.hover}`
                          : 'none',
                      })}
                      {...provided.droppableProps}
                    >
                      {available.length ? (
                        available.map((auth, index) => (
                          <Draggable
                            draggableId={`available-${auth.productAuthorizationId}${
                              auth.supplierDirectedBlend ? '-SDB' : ''
                            }`}
                            index={index}
                            isDragDisabled={isReadOnly}
                            key={`available-${auth.productAuthorizationId}${
                              auth.supplierDirectedBlend ? '-SDB' : ''
                            }`}
                          >
                            {(_provided, snapshot) => (
                              <div
                                ref={_provided.innerRef}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {..._provided.draggableProps}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {..._provided.dragHandleProps}
                              >
                                <AuthSequencingItem
                                  canEdit={!isReadOnly}
                                  index={index}
                                  inventoryOwner={auth.inventoryOwner?.nameShort}
                                  isActive={auth.active}
                                  isChanged={false}
                                  isDragging={snapshot.isDragging}
                                  onAddSeq={() => handleAddToFutureSeq(auth)}
                                  supplierDirectedBlend={auth.supplierDirectedBlend}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <Stack alignItems="center" direction="row" gap={1}>
                          {_snapshot.isDraggingOver ? (
                            <></>
                          ) : (
                            <Box
                              sx={{
                                ...futureDrawerStyles.emptyStateContentSx,
                                ...futureDrawerStyles.emptyStateStyles,
                              }}
                            >
                              <Typography
                                sx={futureDrawerStyles.emptyStateMessageSx}
                                variant="body2"
                              >
                                All Inventory Owners are sequenced.
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Box>
            </DragDropContext>
          </Box>
        </DrawerLayoutWrapper>
      </Box>
    </ClickAwayListener>
  );
};

export default EditFutureSequenceDrawer;

EditFutureSequenceDrawer.propTypes = {
  affiliate: shape({
    customerIdName: string,
    dataCompanyId: number,
  }),
  isReadOnly: bool,
  locationId: number.isRequired,
  onClose: func.isRequired,
  productId: number.isRequired,
};

EditFutureSequenceDrawer.defaultProps = {
  affiliate: null,
  isReadOnly: false,
};
